import React, { useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Facebook,
  Instagram,
  Linkedin,
  MessageCircle,
} from "lucide-react";
import apiClient from "@/lib/api/base";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [isSuccess, setIsSuccess] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setStatusMessage(null); // Clear status message on input change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage(null);
    setIsSuccess(null);

    try {
      await apiClient.post("/api/inquiries", formData);
      setStatusMessage("Message sent successfully!");
      setIsSuccess(true);
      setFormData({ name: "", email: "", subject: "", message: "" }); // Reset form
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setStatusMessage("An error occurred. Please try again later.");
      setIsSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 bg-[url('/public/home_background.avif')] bg-cover bg-center bg-no-repeat">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-blue-900/80 to-teal-800/80"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-teal-400/20 to-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>

      <div className="relative z-10 flex justify-center items-center min-h-screen p-5">
        <div className="flex flex-col lg:flex-row w-full max-w-7xl shadow-2xl rounded-2xl overflow-hidden backdrop-blur-sm bg-white/10 border border-white/20">
          {/* Contact Information Section */}
          <div className="bg-[#0097b3] text-white p-8 lg:p-12 lg:w-2/5 flex flex-col justify-center items-start relative overflow-hidden">
            <div className="relative z-10 w-full">
              <div className="inline-block p-3 bg-white/20 rounded-lg mb-4">
                <MessageCircle className="w-8 h-8" />
              </div>
              <h2 className="text-3xl lg:text-5xl mb-6 font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                Get in Touch
              </h2>
              <p className="text-lg lg:text-xl leading-relaxed mb-8 text-blue-100">
                Looking for tenants, buyers or commercial properties? Our team
                of professionals is ready to assist you with personalized
                solutions.
              </p>

              {/* Contact Details */}
              <div className="space-y-6">
                <div className="flex items-start group hover:bg-white/10 p-4 rounded-lg transition-all duration-300">
                  <div className="bg-white/20 p-3 rounded-full mr-4 group-hover:scale-110 transition-transform duration-300">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Address</h3>
                    <p className="text-blue-100 leading-relaxed">
                      932A 3, 1 Kotte Rd
                      <br />
                      Sri Jayawardenepura Kotte
                      <br />
                      10100, Sri Lanka
                    </p>
                  </div>
                </div>

                <div className="flex items-start group hover:bg-white/10 p-4 rounded-lg transition-all duration-300">
                  <div className="bg-white/20 p-3 rounded-full mr-4 group-hover:scale-110 transition-transform duration-300">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Email</h3>
                    <p className="text-blue-100">info@commercialproperty.lk</p>
                  </div>
                </div>

                <div className="flex items-start group hover:bg-white/10 p-4 rounded-lg transition-all duration-300">
                  <div className="bg-white/20 p-3 rounded-full mr-4 group-hover:scale-110 transition-transform duration-300">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Phone</h3>
                    <p className="text-blue-100">077 780 1742 | 011 711 0858</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-10 pt-8 border-t border-white/20">
                <h3 className="text-lg font-semibold mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  {[Facebook, Instagram, MessageCircle, Linkedin].map(
                    (Icon, index) => (
                      <div key={index} className="group cursor-pointer">
                        <div className="bg-white/20 p-3 rounded-full hover:bg-white/30 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
                          <Icon className="w-6 h-6" />
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="bg-white/95 backdrop-blur-sm p-8 lg:p-12 lg:w-3/5 flex flex-col justify-center items-center relative">
            <div className="w-full max-w-2xl">
              <div className="text-center mb-10">
                <h2 className="text-3xl lg:text-5xl mb-4 text-[#0097b3] font-bold">
                  Let's Connect
                </h2>
                <p className="text-gray-600 text-lg">
                  We'd love to hear from you. Send us a message and we'll
                  respond as soon as possible.
                </p>
              </div>

              {statusMessage && (
                <div
                  className={`mb-6 p-4 rounded-lg text-center ${
                    isSuccess
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {statusMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name and Email Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField("name")}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Your Name"
                      className="w-full p-4 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 focus:border-[#0097b3] focus:outline-none focus:ring-2 focus:ring-[#0097b3]/20 bg-white/80 backdrop-blur-sm"
                      required
                    />
                    {focusedField === "name" && (
                      <div className="absolute -top-2 left-3 bg-[#0097b3] text-white px-2 py-1 rounded text-sm animate-pulse">
                        Required
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Email Address"
                      className="w-full p-4 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 focus:border-[#0097b3] focus:outline-none focus:ring-2 focus:ring-[#0097b3]/20 bg-white/80 backdrop-blur-sm"
                      required
                    />
                    {focusedField === "email" && (
                      <div className="absolute -top-2 left-3 bg-[#0097b3] text-white px-2 py-1 rounded text-sm animate-pulse">
                        Required
                      </div>
                    )}
                  </div>
                </div>

                {/* Subject */}
                <div className="relative">
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("subject")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Subject"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 focus:border-[#0097b3] focus:outline-none focus:ring-2 focus:ring-[#0097b3]/20 bg-white/80 backdrop-blur-sm"
                    required
                  />
                  {focusedField === "subject" && (
                    <div className="absolute -top-2 left-3 bg-[#0097b3] text-white px-2 py-1 rounded text-sm animate-pulse">
                      Required
                    </div>
                  )}
                </div>

                {/* Message */}
                <div className="relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("message")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Your Message"
                    rows={6}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 focus:border-[#0097b3] focus:outline-none focus:ring-2 focus:ring-[#0097b3]/20 bg-white/80 backdrop-blur-sm resize-y"
                    required
                  ></textarea>
                  {focusedField === "message" && (
                    <div className="absolute -top-2 left-3 bg-[#0097b3] text-white px-2 py-1 rounded text-sm animate-pulse">
                      Required
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative bg-[#0097b3] text-white py-4 px-8 rounded-xl text-lg font-semibold transition-all duration-300 hover:bg-[#008099] hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px] flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span>Send Message</span>
                        <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
